function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import * as React from "react";
import { useCombobox } from "downshift";
import { OverlayContainer, useOverlayPosition } from "@react-aria/overlays";
import { useId } from "@react-aria/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import { renderHelperTextDefault } from "../_internal/HelperText";
import { Label } from "../_internal/Label";
import { getOptionLabelDefault, renderOptionDefault } from "../_internal/Option";
import { guessOverlayPosition } from "../_internal/Overlay";
import { cx } from "../../helpers/utils/cx";
import { noopNull } from "../../helpers/utils/noop";
import { KALEP_BOTTOMSHEET_CONTENT_ID } from "../BottomSheet/BottomSheet";
import { ComboBoxTrigger } from "./ComboBoxTrigger";
import { filterOptionsDefault, virtualizerEstimateHeightBySize } from "./helpers";
const DEFAULT_VIRTUALIZER_OVERSCAN = 4;
const VIRTUALIZER_VERTICAL_PADDING = 8;
export const DEFAULT_LOADING_SPINNER_ARIA_LABEL = "loading results";
export const DEFAULT_TOGGLE_BUTTON_ARIA_LABEL = "toggle options list";
export const BaseComboBox = /*#__PURE__*/React.forwardRef(
// TODO: Proper ref forwarding and merging for Combobox
(props, _forwardedRef) => {
  const {
    clearable = true,
    defaultInputValue,
    defaultOpen,
    defaultValue,
    disableCloseOnSelect,
    disabled,
    error,
    filterOptions = filterOptionsDefault,
    freeSolo,
    fullWidth,
    getDropdownProps,
    getOptionLabel = getOptionLabelDefault,
    id,
    infoText,
    inputValue: controlledInputValue,
    label,
    loading,
    loadingSpinnerAriaLabel,
    multiple,
    multiline,
    name,
    noResultsMessage,
    onChange,
    onClear,
    onDelete,
    onInputChange,
    open,
    options,
    overlayConfig,
    placeholder,
    renderOption = renderOptionDefault,
    renderHelperText = renderHelperTextDefault,
    renderStartSlot,
    renderEndSlot,
    renderOverlayFooter = noopNull,
    required,
    rootClassName,
    shouldRenderEndSlotIcon,
    size = "md",
    toggleButtonAriaLabel,
    value: valueProp,
    portalContainer
  } = props;
  const internalRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const listParentRef = React.useRef(null);
  const toggleButtonRef = React.useRef(null);
  const generatedId = useId(id);
  const comboBoxId = id ?? `kalep-combo-box-${generatedId}`;
  const noResultsMessageId = id ?? `kalep-combo-box-no-results-message-${generatedId}`;
  const helperTextId = `${comboBoxId}-helper-text`;
  const [filteredOptions, setFilteredOptions] = React.useState(options);
  const count = filteredOptions.length;
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => listParentRef.current,
    estimateSize: () => virtualizerEstimateHeightBySize(size),
    overscan: DEFAULT_VIRTUALIZER_OVERSCAN
  });
  const virtualItems = virtualizer.getVirtualItems();
  const useComboboxStateReducer = React.useCallback((state, actionAndChanges) => {
    const shouldStayOpenOnSelect = disableCloseOnSelect === undefined ? multiple : disableCloseOnSelect;
    switch (actionAndChanges.type) {
      // Do not close the menu after selection, reset input value for multiple select
      case useCombobox.stateChangeTypes.InputKeyDownEnter:
      case useCombobox.stateChangeTypes.ItemClick:
        return _objectSpread(_objectSpread({}, actionAndChanges.changes), {}, {
          isOpen: shouldStayOpenOnSelect,
          highlightedIndex: state.highlightedIndex,
          inputValue: multiple ? "" : actionAndChanges.changes.inputValue
        });
      case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
        {
          return _objectSpread(_objectSpread({}, actionAndChanges.changes), {}, {
            // In multiple Combobox this state change swallows the first character typed into the input after selection
            inputValue: multiple ? state.inputValue : actionAndChanges.changes.inputValue
          });
        }
      default:
        return actionAndChanges.changes;
    }
  }, [multiple, disableCloseOnSelect]);
  const {
    isOpen,
    inputValue,
    highlightedIndex,
    selectedItem,
    getMenuProps,
    getItemProps,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    openMenu,
    closeMenu,
    selectItem
  } = useCombobox({
    id: comboBoxId,
    items: filteredOptions,
    itemToString(option) {
      if (option?.isCustom) {
        return String(option.value) ?? "";
      }
      return option ? getOptionLabel(option) : "";
    },
    isItemDisabled(option) {
      return !!option.disabled;
    },
    onIsOpenChange(changes) {
      if (changes.isOpen && changes.highlightedIndex !== undefined && changes.highlightedIndex >= 0) {
        virtualizer.scrollToIndex(changes.highlightedIndex);
      }
    },
    onHighlightedIndexChange(changes) {
      if ((changes.type === useCombobox.stateChangeTypes.InputKeyDownArrowUp || changes.type === useCombobox.stateChangeTypes.InputKeyDownArrowDown) && changes.highlightedIndex !== undefined && changes.highlightedIndex !== highlightedIndex) {
        virtualizer.scrollToIndex(changes.highlightedIndex);
      }
    },
    onStateChange(changes) {
      switch (changes.type) {
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          {
            if (changes.selectedItem !== undefined && typeof onChange === "function") {
              onChange(changes.selectedItem?.isCustom ? {
                value: changes.selectedItem.value
              } : changes.selectedItem);
            }
            setFilteredOptions(options);
            break;
          }
        case useCombobox.stateChangeTypes.InputChange:
          {
            if (controlledInputValue === undefined) {
              setFilteredOptions(filterOptions(options, {
                inputValue: changes.inputValue || "",
                getOptionLabel,
                freeSolo
              }));
            }
            break;
          }
        default:
          break;
      }
    },
    stateReducer: useComboboxStateReducer,
    selectedItem: multiple ? null : valueProp,
    // Downshift instructs to set selectedItem null for multicombobox
    initialSelectedItem: defaultValue,
    inputValue: controlledInputValue,
    initialInputValue: defaultInputValue,
    initialIsOpen: defaultOpen,
    isOpen: open
  });
  const handleClear = React.useCallback(() => {
    selectItem(null);
    onChange?.(null);
    onClear?.();
  }, [onChange, selectItem, onClear]);
  React.useEffect(() => {
    setFilteredOptions(filterOptions(options, {
      inputValue: controlledInputValue ?? "",
      getOptionLabel,
      freeSolo
    }));
  }, [controlledInputValue, filterOptions, freeSolo, getOptionLabel, options]);
  const {
    overlayProps
  } = useOverlayPosition(_objectSpread({
    targetRef: internalRef,
    overlayRef: dropdownRef,
    isOpen,
    // Default
    placement: "bottom",
    offset: 4
  }, overlayConfig?.position || {}));
  if (overlayProps?.style && overlayProps.style.top == null && overlayProps.style.bottom == null && isOpen && internalRef.current) {
    // FIXME: Hack to prevent page from scrolling to the end when Combobox opens for the 1st time
    // More context: https://github.com/bolteu/kalep/pull/730
    overlayProps.style.top = guessOverlayPosition(internalRef.current);
  }
  const menuA11yProps = getMenuProps({
    ref: dropdownRef
  }, {
    suppressRefError: true
  });
  const shouldShowNoResultsMessage = isOpen && filteredOptions.length === 0 && !!noResultsMessage && !freeSolo;
  const triggerProps = {
    clearable,
    closeMenu,
    comboBoxId,
    controlledInputValue,
    disabled,
    error,
    fullWidth,
    generatedId,
    getInputProps: getInputProps,
    // TODO: Sth wrong with types here
    getToggleButtonProps: getToggleButtonProps,
    // TODO: Sth wrong with types here
    getDropdownProps,
    getOptionLabel,
    id,
    inputValue,
    inputWrapperRef: internalRef,
    isOpen,
    loading,
    loadingSpinnerAriaLabel,
    multiple,
    multiline,
    name,
    noResultsMessageId,
    onClear: handleClear,
    onDelete,
    onInputChange,
    openMenu,
    placeholder,
    renderStartSlot,
    renderEndSlot,
    required,
    shouldShowNoResultsMessage,
    shouldRenderEndSlotIcon,
    size,
    toggleButtonAriaLabel,
    toggleButtonRef,
    value: multiple ? valueProp : selectedItem
  };
  return /*#__PURE__*/React.createElement("div", {
    className: cx("min-w-0", rootClassName)
  }, /*#__PURE__*/React.createElement("div", {
    className: cx("flex flex-col gap-2", fullWidth ? "w-full" : "w-96")
  }, label && /*#__PURE__*/React.createElement(Label, {
    text: label,
    infoText: infoText,
    required: required,
    disabled: disabled,
    htmlProps: getLabelProps()
  }), /*#__PURE__*/React.createElement(ComboBoxTrigger, triggerProps), renderHelperText(_objectSpread(_objectSpread({}, props), {}, {
    helperTextId
  }), renderHelperTextDefault)), isOpen && /*#__PURE__*/React.createElement(OverlayContainer, {
    portalContainer: portalContainer || internalRef?.current?.closest("dialog") || internalRef?.current?.closest(`#${KALEP_BOTTOMSHEET_CONTENT_ID}`) || document.body
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: cx("absolute z-50", "max-h-80 w-max shadow-md rounded-md")
  }, menuA11yProps, {
    style: _objectSpread({
      minWidth: overlayConfig?.minWidth ? `${overlayConfig.minWidth}px` : "initial",
      maxWidth: overlayConfig?.maxWidth ? `${overlayConfig.maxWidth}px` : "initial",
      width: internalRef.current?.clientWidth || "initial"
    }, overlayProps.style)
  }), /*#__PURE__*/React.createElement("div", {
    ref: listParentRef,
    className: cx("bg-layer-floor-3 overflow-y-auto max-h-80 rounded-md w-full overscroll-none", fullWidth && "w-full")
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- in case it's scrollable.
    ,
    tabIndex: 0
  }, /*#__PURE__*/React.createElement("div", {
    className: cx("relative"),
    style: {
      height: shouldShowNoResultsMessage ? undefined : virtualizer.getTotalSize() + VIRTUALIZER_VERTICAL_PADDING
    }
  }, /*#__PURE__*/React.createElement("ul", {
    className: cx("list-none py-1 px-0 m-0 empty:py-0", "max-h-full overflow-y-hidden", "cursor-pointer"),
    style: {
      transform: `translateY(${virtualItems[0]?.start ?? 0}px)`
    },
    role: "listbox"
  }, virtualItems.map(virtualItem => {
    const {
      index
    } = virtualItem;
    const option = filteredOptions[index];
    const optionLabel = getOptionLabel(option);
    const optionProps = _objectSpread(_objectSpread({}, getItemProps({
      item: option,
      index
    })), {}, {
      highlighted: highlightedIndex === index,
      selected: selectedItem === option,
      option,
      label: optionLabel,
      key: virtualItem.key.toString(),
      ref: virtualizer.measureElement,
      "data-index": virtualItem.index,
      size
    });
    return typeof renderOption === "function" ? renderOption(optionProps, renderOptionDefault) : renderOptionDefault(optionProps);
  })), shouldShowNoResultsMessage && /*#__PURE__*/React.createElement("div", {
    id: noResultsMessageId,
    className: cx("py-2 px-6 flex items-center text-primary")
  }, noResultsMessage)), renderOverlayFooter(props, noopNull)))));
});